import React, { useState, useEffect, useMemo, useTransition, useDeferredValue } from 'react';
import { Link } from 'react-router-dom';
import './ShinyShowcase.css';

// Preload sparkle gifs on page load
const sparkleGifs = [require('./flair/sparkle 2.gif')];
sparkleGifs.forEach(src => {
  const img = new window.Image();
  img.src = src;
});

export const ShinySprite = React.memo(function ShinySprite({ shiny, perfMode }) {
  // shiny: { gif: string, type?: string[] }
  const [showSparkle, setShowSparkle] = useState(false);
  const [sparkleActive, setSparkleActive] = useState(false);
  const [sparkleSrc, setSparkleSrc] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = React.useRef(null);
  const imgSrc = isVisible ? require(`./sprites/shiny/${shiny.gif}`) : undefined;

  const handleMouseEnter = () => {
    if (!sparkleActive) {
      setSparkleSrc(require('./flair/sparkle 2.gif') + '?t=' + Date.now());
      setShowSparkle(true);
      setSparkleActive(true);
    }
  };

  const handleMouseLeave = () => {
    setShowSparkle(false);
    setSparkleActive(false);
    setSparkleSrc(null);
  };

  // Hide sparkle after animation duration (e.g., 500ms)
  useEffect(() => {
    let timeout;
    if (showSparkle) {
      timeout = setTimeout(() => {
        setShowSparkle(false);
        setSparkleActive(false);
        setSparkleSrc(null);
      }, 800);
    }
    return () => clearTimeout(timeout);
  }, [showSparkle]);

  // Lazy-load: only load GIF once the sprite is near viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Attribute overlays
  const isSecret = shiny.type?.includes('secret');
  const isAlpha = shiny.type?.includes('shiny alpha');
  const isSwarm = shiny.type?.includes('swarm');
  const isEgg = shiny.type?.includes('egg');
  const isSafari = shiny.type?.includes('safari');
  const isSafariFlee = shiny.type?.includes('safari-flee');
  const isLost = shiny.type?.includes('lost');
  const isHeadbutt = shiny.type?.includes('headbutt');
  // Lost shinies should use the exact same visual as safari-flee
  const isFleeVisual = isSafariFlee || isLost;

  return (
    <div
      className={`shiny-container${isAlpha ? ' shiny-alpha' : ''}${isFleeVisual ? ' shiny-safari-flee' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={isAlpha ? { zIndex: 2, position: 'relative' } : {}}
      ref={containerRef}
    >
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <img
          src={imgSrc || undefined}
          alt={shiny.gif.replace('.gif', '')}
          className={`shiny-gif${isAlpha ? ' shiny-gif-glow' : ''}${isFleeVisual ? ' shiny-gif-safari-flee' : ''}`}
          style={{marginBottom: 0, filter: 'drop-shadow(0 1px 6px #000)', display: 'block'}}
          loading="lazy"
          decoding="async"
          width={96}
          height={96}
        />
        {/* Overlay icons stacked in top right, stacking downward */}
        {(() => {
          // Collect all active overlays in a fixed order
          const overlays = [];
          if (isSecret) overlays.push({ src: require('./flair/secret.png'), alt: 'Secret Shiny', className: 'shiny-overlay-secret', size: 22, type: 'secret' });
          if (isEgg) overlays.push({ src: require('./flair/egg.png'), alt: 'Egg Shiny', className: 'shiny-overlay-egg', size: 16, type: 'egg' });
          if (isSwarm) overlays.push({ src: require('./flair/grass.png'), alt: 'Swarm Shiny', className: 'shiny-overlay-swarm', size: 14, type: 'swarm' });
          // Only add safari ball if not lost shiny
          if ((isSafari || isSafariFlee) && !isLost) overlays.push({ src: require('./flair/safari.png'), alt: 'Safari Shiny', className: 'shiny-overlay-safari', size: 22, type: 'safari' });
          // New overlays
          if (shiny.type?.includes('mystball')) overlays.push({ src: require('./flair/ball.png'), alt: 'MystBalls Shiny', className: 'shiny-overlay-mystball', size: 14, type: 'mystball' });
          if (shiny.type?.includes('fossil')) overlays.push({ src: require('./flair/helix_fossil.png'), alt: 'Fossil Shiny', className: 'shiny-overlay-fossil', size: 18, type: 'fossil' });
          if (shiny.type?.includes('honey')) overlays.push({ src: require('./flair/honey.png'), alt: 'Honey Shiny', className: 'shiny-overlay-honey', size: 18, type: 'honey' });
          if (shiny.type?.includes('headbutt')) overlays.push({ src: require('./flair/headbutt.png'), alt: 'Headbutt Shiny', className: 'shiny-overlay-headbutt', size: 18, type: 'headbutt' });
          // Per-icon-type manual right offsets (tweak as needed)
          const typeRightOffsets = {
            secret: 1,
            egg: 3,
            swarm: 4,
            safari: 0,
            mystball: 3,
            fossil: 0,
            honey: 0,
            headbutt: 0,
          };
          return overlays.length > 0 ? (
            <div style={{ 
              position: 'absolute', 
              top: -15,
              right: -15,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '2px',
              zIndex: 5,
              pointerEvents: 'none',
              width: '40px',
              height: 'auto'
            }}>
              {overlays.map((icon) => (
                <img
                  key={icon.alt}
                  src={icon.src}
                  alt={icon.alt}
                  className={`shiny-overlay-corner ${icon.className}`}
                  style={{ 
                    position: 'relative',
                    right: typeRightOffsets[icon.type] || 0,
                    width: icon.size, 
                    height: icon.size, 
                    display: 'block',
                    objectFit: 'contain'
                  }}
                />
              ))}
            </div>
          ) : null;
        })()}
        {/* Sparkle effect gif, only show when hovering, force remount with key to restart */}
        {showSparkle && !isFleeVisual && (
          <img
            src={sparkleSrc}
            className="sparkle-effect"
            alt="sparkle effect"
            draggable={false}
            style={{ width: '100px', height: '100px', objectFit: 'contain', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 10 }}
          />
        )}
      </div>
    </div>
  );
});

export default function ShinyShowcase() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search functionality
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Sorting functionality
  const [sortMode, setSortMode] = useState(() => localStorage.getItem('radiance_sort_mode') || 'alpha');
  const [colorSortEnabled, setColorSortEnabled] = useState(() => {
    const saved = localStorage.getItem('radiance_color_sort');
    return saved === 'true'; // Default to false if not set
  });
  
  // Use deferred value and transition hooks - must be declared early
  const deferredSearch = useDeferredValue(debouncedSearch);
  const [isPending, startTransition] = useTransition();
  
  useEffect(() => {
    localStorage.setItem('radiance_sort_mode', sortMode);
  }, [sortMode]);
  useEffect(() => {
    localStorage.setItem('radiance_color_sort', colorSortEnabled.toString());
  }, [colorSortEnabled]);
  useEffect(() => {
    const handler = setTimeout(() => {
      startTransition(() => {
        setDebouncedSearch(search);
      });
    }, 200);
    return () => clearTimeout(handler);
  }, [search, startTransition]);
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('https://radiance-backend-production.up.railway.app/api/users');
      if (!res.ok) throw new Error('Failed to fetch');
      let data = await res.json();
      setUsers(data);
      setLoading(false);
    } catch (err) {
      setError('Could not load users.');
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  // Lightweight color sort key: simple hue approximation from RGB
  // Much faster than full HSL conversion - just calculates a rough hue value
  const getColorSortKey = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max === min) return 0;
    const d = max - min;
    let h = 0;
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = Math.round(h * 60); // Convert to degrees (0-360)
    if (h < 0) h += 360;
    // Return a sortable key: hue * 10000 + saturation approximation
    const satApprox = Math.round((d / max) * 100);
    return h * 10000 + satApprox;
  };

  // Lazy load pokedex only when color sort is enabled
  const [pokedexData, setPokedexData] = useState(null);
  useEffect(() => {
    if (colorSortEnabled && !pokedexData) {
      import('./data/pokedex.json').then(module => setPokedexData(module.default));
    }
  }, [colorSortEnabled, pokedexData]);

  // Pre-computed color map: build once when pokedex loads
  const colorMapRef = React.useRef(new Map());
  
  // Build color map when pokedex data is available
  useEffect(() => {
    if (pokedexData && colorSortEnabled) {
      const map = new Map();
      pokedexData.forEach(p => {
        const normalizedName = p.image.toLowerCase().replace('.gif', '');
        // Store for exact match
        map.set(normalizedName, p.color);
        // Also store base name (without gender suffix) for fallback
        const baseName = normalizedName.replace(/[-_](f|m)$/i, '');
        if (baseName !== normalizedName && !map.has(baseName)) {
          map.set(baseName, p.color);
        }
      });
      colorMapRef.current = map;
    }
  }, [pokedexData, colorSortEnabled]);

  // Lightweight color sorting: pre-compute sort keys, then simple numeric sort
  const sortShiniesByColor = useMemo(() => {
    if (!colorSortEnabled || !pokedexData) {
      return (shinies) => shinies;
    }
    
    return (shinies) => {
      // Pre-compute sort keys for all shinies in one pass
      const withKeys = shinies.map(shiny => {
        const gifName = typeof shiny === 'string' ? shiny : shiny.gif;
        const normalizedName = gifName.replace('.gif', '').toLowerCase();
        
        // Fast lookup: try exact match, then base name
        let color = colorMapRef.current.get(normalizedName);
        if (!color) {
          const baseName = normalizedName.replace(/[-_](f|m)$/i, '');
          color = colorMapRef.current.get(baseName) || '#000000';
        }
        
        return {
          shiny,
          sortKey: getColorSortKey(color)
        };
      });
      
      // Simple numeric sort (much faster than complex comparisons)
      withKeys.sort((a, b) => a.sortKey - b.sortKey);
      
      // Return original shiny objects
      return withKeys.map(item => item.shiny);
    };
  }, [colorSortEnabled, pokedexData]);

  // Memoize expensive filtering and sorting operations
  const filteredUsers = useMemo(() => {
    return users
      .filter(u => !u.guessGameOnly) // Exclude Guess the Hours Only users
      .filter(u => u.username.toLowerCase().includes(deferredSearch.toLowerCase()))
      .sort((a, b) => {
        if (sortMode === 'alpha') {
          return a.username.localeCompare(b.username);
        } else {
          return (b.count || 0) - (a.count || 0) || a.username.localeCompare(b.username);
        }
      });
  }, [users, deferredSearch, sortMode]);
  return (
    <div className="showcase-container page">
      <header className="showcase-banner" id="home">
        <h1>Team Rädiance Shiny Showcase</h1>
      </header>
      <section className="showcase-intro" id="about">
        <p>
          Welcome to the official Team Rädiance Shiny Showcase! Here we display our members' PokeMMO shiny achievements.
        </p>
        <div className="showcase-controls">
          <input
            type="text"
            placeholder="Search user..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            className="sort-toggle-btn"
            onClick={() => setSortMode(m => m === 'alpha' ? 'count' : 'alpha')}
          >
            Sort: {sortMode === 'alpha' ? 'A-Z' : 'Most Shinies'}
          </button>
          <button
            className={`sort-toggle-btn ${colorSortEnabled ? 'color-sort-active' : ''}`}
            onClick={() => setColorSortEnabled(c => !c)}
          >
            {colorSortEnabled ? 'Sort by Color' : 'Sort by Order'}
          </button>
        </div>
      </section>
      <section className="showcase-list" id="showcase">
        {loading ? (
          <div style={{textAlign: 'center', color: '#ffd700', fontSize: '1.2rem', margin: '2rem 0'}}>Loading...</div>
        ) : error ? (
          <div style={{textAlign: 'center', color: '#ff4d4d', fontSize: '1.2rem', margin: '2rem 0'}}>{error}</div>
        ) : (
          filteredUsers.map((player, idx) => (
            <div className="player-section" key={idx}>
              <h2 className="player-heading" style={{display:'flex',alignItems:'center',gap:8}}>
                <img src={require('./flair/giphy.gif')} alt="flair" style={{height:32,marginRight:4}} />
                <Link 
                  to={`/${player.username}`} 
                  style={{color: 'inherit', textDecoration: 'none'}}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {player.username}
                </Link>
                <span className="shiny-count">({player.count} Shinies):</span>
                <img src={require('./flair/giphy.gif')} alt="flair" style={{height:32,marginLeft:4}} />
              </h2>
              <hr className="player-divider" />
              <div className="player-shinies">
                {sortShiniesByColor(player.shinies).map((shiny, i) => {
                  const gifName = typeof shiny === 'string' ? shiny : shiny.gif;
                  return <ShinySprite key={`${player.username}-${gifName}-${i}`} shiny={typeof shiny === 'string' ? { gif: shiny } : shiny} />;
                })}
              </div>
            </div>
          ))
        )}
      </section>
      <section className="shiny-board" id="board">
      </section>
    </div>
  );
}